import { useLocale } from './i18n';
import { Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';
import { lazy, Suspense, type ReactElement } from 'react';
import { useSession } from './hooks/session';
import { SiteChrome } from './components/SiteChrome';
import { Spinner } from './components/ui';
import { Landing, PUBLIC_PAGES, RETIRED_PUBLIC_PATHS } from './pages/Landing';
import { SignIn } from './pages/SignIn';
import { ResetPassword } from './pages/ResetPassword';
import { Download } from './pages/Download';
import { Children } from './pages/Children';
import { Review } from './pages/Review';
import { Session } from './pages/Session';
import { Report } from './pages/Report';
import { Checkout, CheckoutRedirect } from './pages/Checkout';
import { Plus } from './pages/Plus';
import { MyReports } from './pages/MyReports';
import { Transactions } from './pages/Transactions';
import { Settings } from './pages/Settings';
import { NotFound } from './pages/NotFound';
import { GuestStart } from './pages/GuestStart';
import { FinishCheckin } from './pages/FinishCheckin';
import { guestMayOpen } from './features/auth/guest';
import { FamilyHome } from './pages/FamilyHome';
import { Programme, ProgrammeIndex } from './pages/Programme';
import { Activity } from './pages/Activity';
import { ScrollManager } from './components/layout/ScrollManager';
import { RouteSeo } from './seo/RouteSeo';

// Long-form public pages load on demand so their text stays out of the first-paint bundle.
const Legal = lazy(() => import('./pages/Legal'));
const Trust = lazy(() => import('./pages/Trust'));
const DataRequest = lazy(() => import('./pages/DataRequest'));
const Faq = lazy(() => import('./pages/Faq'));
const LearnIndex = lazy(() => import('./pages/Learn'));
const LearnArticle = lazy(() => import('./pages/Learn').then((m) => ({ default: m.LearnArticle })));
const ChildProfile = lazy(() => import('./pages/ChildProfile'));
const SharedReport = lazy(() => import('./pages/SharedReport'));
const JoinFamily = lazy(() => import('./pages/JoinFamily'));
// The admin console is its own chunk: parents never download it.
const AdminApp = lazy(() => import('./admin/AdminApp'));
const FamilyHub = lazy(() => import('./pages/Family'));
// The home assessment is its own chunk too: first paint (landing, sign-in) never needs it.
const Assessment = lazy(() => import('./pages/Assessment').then((m) => ({ default: m.Assessment })));
const FamilyReports = lazy(() => import('./pages/Family').then((m) => ({ default: m.FamilyReports })));
const FamilyReport = lazy(() => import('./pages/Family').then((m) => ({ default: m.FamilyReport })));

const PageLoading = () => (
  <div className="flex min-h-screen items-center justify-center text-lagoon-600">
    <Spinner />
  </div>
);

/** Old `/legal/:page` links keep working. */
const LegacyLegal = () => {
  const { page } = useParams();
  return <Navigate to={page === 'terms' || page === 'refunds' ? `/${page}` : '/privacy'} replace />;
};
const RequireAuth = ({ children }: { children: ReactElement }) => {
  const { account, loading } = useSession();
  const location = useLocation();

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center text-lagoon-600">
        <Spinner />
      </div>
    );
  }
  if (!account) {
    return <Navigate to="/signin" replace state={{ from: location.pathname + location.search }} />;
  }
  // A check-in started without an account: the child step and the questions are open; everything
  // else (the result first of all) asks for a sign-up, and comes back here afterwards.
  if (account.guest && !guestMayOpen(location.pathname)) {
    return <Navigate to="/signup" replace state={{ from: location.pathname + location.search }} />;
  }
  return children;
};

export const App = () => {
  // Pages hold server text (questions, reports) in the language they were fetched in; switching
  // language remounts them so they fetch again in the new one.
  const { locale } = useLocale();
  return (
  <>
  <ScrollManager />
  <RouteSeo />
  <SiteChrome />
  <Suspense fallback={<PageLoading />}>
  <Routes key={locale}>
    <Route path="/" element={<Landing />} />
    {PUBLIC_PAGES.map((p) => (
      <Route key={p.path} path={p.path} element={<Landing view={p.view} />} />
    ))}
    {Object.entries(RETIRED_PUBLIC_PATHS).map(([from, to]) => (
      <Route key={from} path={from} element={<Navigate to={to} replace />} />
    ))}
    <Route path="/signin" element={<SignIn />} />
    <Route path="/signup" element={<SignIn mode="signup" />} />
    <Route path="/start" element={<GuestStart />} />
    <Route path="/reset" element={<ResetPassword />} />
    <Route path="/d/:token" element={<Download />} />
    <Route path="/privacy" element={<Legal page="privacy" />} />
    <Route path="/terms" element={<Legal page="terms" />} />
    <Route path="/refunds" element={<Legal page="refunds" />} />
    <Route path="/legal/:page" element={<LegacyLegal />} />
    <Route path="/trust" element={<Trust />} />
    <Route path="/data-request" element={<DataRequest />} />
    <Route path="/faq" element={<Faq />} />
    <Route path="/learn" element={<LearnIndex />} />
    <Route path="/learn/:slug" element={<LearnArticle />} />
    <Route path="/shared/:token" element={<SharedReport />} />
    <Route path="/join/:token" element={<JoinFamily />} />
    <Route
      path="/children/:childId"
      element={
        <RequireAuth>
          <ChildProfile />
        </RequireAuth>
      }
    />
    <Route
      path="/family"
      element={
        <RequireAuth>
          <FamilyHub />
        </RequireAuth>
      }
    />
    <Route
      path="/family/:ownerId"
      element={
        <RequireAuth>
          <FamilyReports />
        </RequireAuth>
      }
    />
    <Route
      path="/family/:ownerId/reports/:reportId"
      element={
        <RequireAuth>
          <FamilyReport />
        </RequireAuth>
      }
    />

    <Route
      path="/home"
      element={
        <RequireAuth>
          <FamilyHome />
        </RequireAuth>
      }
    />
    <Route
      path="/programme"
      element={
        <RequireAuth>
          <ProgrammeIndex />
        </RequireAuth>
      }
    />
    <Route
      path="/programme/:assessmentId"
      element={
        <RequireAuth>
          <Programme />
        </RequireAuth>
      }
    />
    <Route
      path="/programme/:assessmentId/:exerciseId"
      element={
        <RequireAuth>
          <Activity />
        </RequireAuth>
      }
    />
    <Route
      path="/children"
      element={
        <RequireAuth>
          <Children />
        </RequireAuth>
      }
    />
    <Route
      path="/assessments/:assessmentId"
      element={
        <RequireAuth>
          <Assessment />
        </RequireAuth>
      }
    />
    <Route
      path="/assessments/:assessmentId/finish"
      element={
        <RequireAuth>
          <FinishCheckin />
        </RequireAuth>
      }
    />
    <Route
      path="/assessments/:assessmentId/review"
      element={
        <RequireAuth>
          <Review />
        </RequireAuth>
      }
    />
    <Route
      path="/sessions/:assessmentId"
      element={
        <RequireAuth>
          <Session />
        </RequireAuth>
      }
    />
    <Route
      path="/reports/:reportId"
      element={
        <RequireAuth>
          <Report />
        </RequireAuth>
      }
    />
    <Route
      path="/sessions/:assessmentId/checkout"
      element={
        <RequireAuth>
          <Checkout />
        </RequireAuth>
      }
    />
    <Route
      path="/reports/:reportId/checkout"
      element={
        <RequireAuth>
          <CheckoutRedirect />
        </RequireAuth>
      }
    />
    <Route
      path="/plus"
      element={
        <RequireAuth>
          <Plus />
        </RequireAuth>
      }
    />
    <Route
      path="/reports"
      element={
        <RequireAuth>
          <MyReports />
        </RequireAuth>
      }
    />
    <Route
      path="/transactions"
      element={
        <RequireAuth>
          <Transactions />
        </RequireAuth>
      }
    />
    <Route
      path="/settings"
      element={
        <RequireAuth>
          <Settings />
        </RequireAuth>
      }
    />
    <Route
      path="/admin/*"
      element={
        <RequireAuth>
          <AdminApp />
        </RequireAuth>
      }
    />

    <Route path="*" element={<NotFound />} />
  </Routes>
  </Suspense>
  </>
  );
};
